import { useEffect, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router'
import { FiMenu, FiX } from 'react-icons/fi'
import { FaFacebook, FaInstagram, FaXTwitter, FaLinkedin, FaYoutube } from 'react-icons/fa6'
import { Container } from '@/components/common'
import { NAV_LINKS, ROUTES } from '@/constants/routes'
import { cn } from '@/lib/cn'
import logoWhite from '@/assets/logo/mainLogo.png'

const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)'

const socials = [
  { Icon: FaXTwitter, label: 'X (Twitter)', href: 'https://x.com/ibon_world' },
  { Icon: FaInstagram, label: 'Instagram', href: 'https://www.instagram.com/ibonworld/' },
  { Icon: FaFacebook, label: 'Facebook', href: 'https://www.facebook.com/Ibonworld' },
  { Icon: FaYoutube, label: 'YouTube', href: 'https://www.youtube.com/@ibonworld' },
  { Icon: FaLinkedin, label: 'LinkedIn', href: 'https://www.linkedin.com/company/ibonworld/' },
]

const linkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'group rounded-full px-3 py-2 text-xs font-semibold uppercase tracking-wide transition-colors xl:tracking-widest',
    isActive ? 'bg-ibon-sky text-ibon-dark' : 'text-white/70 hover:bg-white/10 hover:text-white',
  )

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false)
  const [open, setOpen] = useState(false)
  const { pathname } = useLocation()
  const toggleRef = useRef<HTMLButtonElement>(null)
  const firstLinkRef = useRef<HTMLAnchorElement>(null)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOpen(false)
  }, [pathname])

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  const wasOpenRef = useRef(false)
  useEffect(() => {
    if (open) {
      firstLinkRef.current?.focus()
      wasOpenRef.current = true
    } else if (wasOpenRef.current) {
      toggleRef.current?.focus()
      wasOpenRef.current = false
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open])

  return (
    <>
      <header
        className={cn(
          'sticky top-0 z-50 transition-all duration-300',
          scrolled || open
            ? 'border-b border-white/10 bg-ibon-dark/95 backdrop-blur-md'
            : 'border-b border-transparent bg-ibon-dark/30 backdrop-blur-sm',
        )}
      >
        <Container className="flex h-24 items-center justify-between gap-4 py-2">
          <NavLink to={ROUTES.HOME} className="relative z-50 shrink-0" aria-label="IBON World home">
            <img src={logoWhite} alt="IBON World" className="h-14 w-auto sm:h-14" />
          </NavLink>

          {/* Desktop navigation */}
          <nav className="hidden flex-1 items-center justify-center gap-1 lg:flex">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === ROUTES.HOME}
                className={linkClass}
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          {/* Right cluster: social icons + mobile toggle */}
          <div className="flex shrink-0 items-center gap-3">
            <div className="hidden items-center gap-2 xl:flex">
              {socials.map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-white/20 text-white/70 transition-colors hover:border-ibon-sky hover:text-ibon-sky"
                >
                  <Icon size={13} />
                </a>
              ))}
            </div>

            <button
              ref={toggleRef}
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? 'Close menu' : 'Open menu'}
              aria-expanded={open}
              aria-controls="mobile-nav-menu"
              className="relative z-50 flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white transition-colors hover:bg-white/5 lg:hidden"
            >
              <span
                style={{
                  opacity: open ? 0 : 1,
                  transform: open ? 'rotate(90deg) scale(0.5)' : 'rotate(0deg) scale(1)',
                  transition: `opacity 0.25s ${EASE}, transform 0.25s ${EASE}`,
                  position: 'absolute',
                }}
              >
                <FiMenu size={22} />
              </span>
              <span
                style={{
                  opacity: open ? 1 : 0,
                  transform: open ? 'rotate(0deg) scale(1)' : 'rotate(-90deg) scale(0.5)',
                  transition: `opacity 0.25s ${EASE}, transform 0.25s ${EASE}`,
                  position: 'absolute',
                }}
              >
                <FiX size={22} />
              </span>
            </button>
          </div>
        </Container>
      </header>

      {/* Mobile backdrop */}
      <div
        aria-hidden="true"
        onClick={() => setOpen(false)}
        className="fixed inset-0 z-30 bg-ibon-midnight/70 backdrop-blur-sm lg:hidden"
        style={{
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: `opacity 0.4s ${EASE}`,
        }}
      />

      {/* Mobile drawer menu */}
      <div
        id="mobile-nav-menu"
        className="fixed inset-y-0 left-0 z-40 flex w-[85%] max-w-sm flex-col bg-ibon-dark shadow-2xl lg:hidden"
        aria-hidden={!open}
        inert={!open}
        style={{
          transform: open ? 'translateX(0)' : 'translateX(-100%)',
          pointerEvents: open ? 'auto' : 'none',
          transition: `transform 0.45s ${EASE}`,
        }}
      >
        <div className="flex-1 overflow-y-auto px-6 pb-10 pt-24">
          <nav className="flex flex-col">
            {NAV_LINKS.map((link, index) => (
              <NavLink
                key={link.to}
                ref={index === 0 ? firstLinkRef : undefined}
                to={link.to}
                end={link.to === ROUTES.HOME}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'border-b border-white/8 py-4 font-display text-2xl font-bold uppercase tracking-tight',
                    isActive ? 'text-ibon-sky' : 'text-white',
                  )
                }
                style={{
                  opacity: open ? 1 : 0,
                  transform: open ? 'translateY(0)' : 'translateY(16px)',
                  transitionProperty: 'opacity, transform, color',
                  transitionDuration: '0.45s, 0.45s, 0.2s',
                  transitionTimingFunction: `${EASE}, ${EASE}, ease`,
                  transitionDelay: open ? `${100 + index * 55}ms` : '0ms',
                }}
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div
            className="mt-8 flex flex-col gap-3"
            style={{
              opacity: open ? 1 : 0,
              transform: open ? 'translateY(0)' : 'translateY(16px)',
              transition: `opacity 0.45s ${EASE}, transform 0.45s ${EASE}`,
              transitionDelay: open ? `${100 + NAV_LINKS.length * 55 + 40}ms` : '0ms',
            }}
          >
            <NavLink
              to={ROUTES.PARTNER}
              onClick={() => setOpen(false)}
              className="rounded-full border border-white/20 px-6 py-3 text-center text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-white/5"
            >
              Partner With Us
            </NavLink>
            <NavLink
              to={ROUTES.DONATE}
              onClick={() => setOpen(false)}
              className="rounded-full bg-ibon-sky px-6 py-3 text-center text-sm font-semibold uppercase tracking-wide text-ibon-dark transition-colors hover:bg-[#6fcdef]"
            >
              Donate Now
            </NavLink>
          </div>

          <div
            className="mt-8 flex flex-wrap items-center gap-3"
            style={{
              opacity: open ? 1 : 0,
              transform: open ? 'translateY(0)' : 'translateY(16px)',
              transition: `opacity 0.45s ${EASE}, transform 0.45s ${EASE}`,
              transitionDelay: open ? `${100 + NAV_LINKS.length * 55 + 120}ms` : '0ms',
            }}
          >
            {socials.map(({ Icon, label, href }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-white/70 transition-colors hover:border-ibon-sky hover:text-ibon-sky"
              >
                <Icon size={16} />
              </a>
            ))}
          </div>
        </div>
      </div>
    </>
  )
}
